/**
 * /settings/team — Admin page for managing users and invitations.
 *
 * Sections:
 *  1. Active users — table of all user accounts with role chips
 *  2. Invite someone — email + role form that posts to POST /auth/invitations
 *  3. Pending invitations — list of open invites the admin can revoke
 *
 * Backend prerequisites (all admin-only):
 *  GET  /api/users                       → list[UserOut]
 *  POST /api/auth/invitations            → InvitationOut
 *  GET  /api/auth/invitations            → list[InvitationListItem]
 *  DELETE /api/auth/invitations/{id}     → 204
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockResetIcon from "@mui/icons-material/LockReset";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { api, errorMessage } from "@/lib/api";
import { meQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/settings/team")({
  component: TeamPage,
});

// ── Types ────────────────────────────────────────────────────────────────────

interface UserOut {
  id: string;
  email: string;
  full_name: string;
  role: "admin" | "operator" | "viewer";
  is_active: boolean;
  mfa_enabled: boolean;
  signup_purpose: "team_member" | "demo_reviewer";
  organization_name: string | null;
  job_title: string | null;
  demo_interest: string | null;
  created_at: string;
  last_login_at: string | null;
}

interface InvitationListItem {
  id: string;
  email: string;
  role: "admin" | "operator" | "viewer";
  created_at: string;
  expires_at: string;
  purpose: "team_member" | "demo_reviewer";
  full_name: string | null;
  organization_name: string | null;
  job_title: string | null;
  demo_interest: string | null;
}

interface InvitationResult {
  email: string;
  role: "admin" | "operator" | "viewer";
  expires_at: string;
  purpose: "team_member" | "demo_reviewer";
  full_name: string | null;
  organization_name: string | null;
  job_title: string | null;
  demo_interest: string | null;
}

const SAMPLE_OPTIONS = [
  ["concrete_pumping", "Concrete pumping"],
  ["concrete_plant", "Concrete plant"],
  ["construction", "Construction"],
  ["manufacturing", "Manufacturing"],
  ["rental", "Equipment rental"],
  ["services", "Services"],
  ["saas", "SaaS"],
  ["hospitality", "Hospitality"],
  ["core", "General accounting"],
] as const;

const SAMPLE_LABELS = Object.fromEntries(SAMPLE_OPTIONS) as Record<string, string>;

// ── Query keys ────────────────────────────────────────────────────────────────

const teamKeys = {
  users: ["team", "users"] as const,
  invitations: ["team", "invitations"] as const,
};

// ── Role display helpers ──────────────────────────────────────────────────────

const ROLE_COLORS = {
  admin: "error",
  operator: "warning",
  viewer: "default",
} as const;

const ROLE_LABELS = {
  admin: "Admin",
  operator: "Operator",
  viewer: "Viewer",
} as const;

function RoleChip({ role }: { role: "admin" | "operator" | "viewer" }) {
  return (
    <Chip
      label={ROLE_LABELS[role] ?? role}
      color={ROLE_COLORS[role] ?? "default"}
      size="small"
    />
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

function TeamPage() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" } | null>(null);

  // Current user — so we never let an admin lock themselves out of their own
  // account (no self-demote, no self-deactivate).
  const meQuery = useQuery(meQueryOptions());
  const myId = meQuery.data?.id;

  // ── Users list ───────────────────────────────────────────────────────────
  const usersQuery = useQuery({
    queryKey: teamKeys.users,
    queryFn: () => api<UserOut[]>("/api/users"),
  });

  // ── Pending invitations ──────────────────────────────────────────────────
  const invitationsQuery = useQuery({
    queryKey: teamKeys.invitations,
    queryFn: () => api<InvitationListItem[]>("/api/auth/invitations"),
  });

  // Defensive: the api() helper occasionally surfaces non-array bodies
  // (auth error envelopes, stringified responses, etc.) which crash the
  // page when we try to .map() over them. Normalise to a strict array.
  const users: UserOut[] = Array.isArray(usersQuery.data) ? usersQuery.data : [];
  const invitations: InvitationListItem[] = Array.isArray(invitationsQuery.data)
    ? invitationsQuery.data
    : [];
  const canInviteReviewers = !!meQuery.data?.is_platform_admin;
  const demoReviewers = users.filter((user) => user.signup_purpose === "demo_reviewer");
  const companyUsers = canInviteReviewers
    ? users.filter((user) => user.signup_purpose !== "demo_reviewer")
    : users;

  // ── Revoke invitation ────────────────────────────────────────────────────
  const revokeMutation = useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/auth/invitations/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.setQueryData<InvitationListItem[]>(teamKeys.invitations, (prev) =>
        prev?.filter((inv) => inv.id !== id) ?? [],
      );
      setToast({ msg: "Invitation revoked.", severity: "success" });
    },
    onError: (err) => setToast({ msg: errorMessage(err), severity: "error" }),
  });

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography variant="h4" fontWeight={700}>
          People & invitations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Invite a private demo reviewer or add someone to a company team, then see what happens next.
        </Typography>
      </Box>

      {/* ── Invite someone ─────────────────────────────────────────────── */}
      {meQuery.isLoading ? (
        <Card variant="outlined"><CardContent><CircularProgress size={20} /></CardContent></Card>
      ) : (
        <InviteCard
        canInviteReviewers={canInviteReviewers}
        onSuccess={(invite) => {
          setToast({ msg: `Invitation sent to ${invite.email}.`, severity: "success" });
          qc.invalidateQueries({ queryKey: teamKeys.invitations });
          qc.invalidateQueries({ queryKey: teamKeys.users });
        }}
        onError={(msg) => setToast({ msg, severity: "error" })}
        />
      )}

      {canInviteReviewers && (
        <DemoReviewersCard
          reviewers={demoReviewers}
          isLoading={usersQuery.isLoading}
          error={usersQuery.error}
          onToast={setToast}
          onChanged={() => qc.invalidateQueries({ queryKey: teamKeys.users })}
        />
      )}

      {/* ── Active users ───────────────────────────────────────────────── */}
      <Card variant="outlined">
        <CardHeader
          avatar={<GroupIcon color="action" />}
          title={canInviteReviewers ? "Company team" : "Active users"}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
          subheader={
            usersQuery.data
              ? `${companyUsers.length} account${companyUsers.length !== 1 ? "s" : ""}`
              : undefined
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {usersQuery.isLoading && <CircularProgress size={20} />}
          {usersQuery.error && (
            <Alert severity="error">{errorMessage(usersQuery.error)}</Alert>
          )}
          {usersQuery.data && companyUsers.length === 0 && (
            <Alert severity="info">
              No company team members yet. Choose <strong>Team member</strong> above to add one.
            </Alert>
          )}
          {usersQuery.data && companyUsers.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>MFA</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companyUsers.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {u.full_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {u.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <RoleChip role={u.role} />
                    </TableCell>
                    <TableCell>
                      {u.mfa_enabled ? (
                        <Chip label="On" color="success" size="small" />
                      ) : (
                        <Chip label="Off" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {u.is_active ? (
                        <Chip label="Active" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Inactive" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <UserRowActions
                        user={u}
                        isSelf={u.id === myId}
                        onToast={setToast}
                        onChanged={() => qc.invalidateQueries({ queryKey: teamKeys.users })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Pending invitations ─────────────────────────────────────────── */}
      <Card variant="outlined">
        <CardHeader
          avatar={<EmailIcon color="action" />}
          title="Pending invitations"
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
          subheader="Invites that haven't been accepted yet"
        />
        <CardContent sx={{ pt: 0 }}>
          {invitationsQuery.isLoading && <CircularProgress size={20} />}
          {invitationsQuery.error && (
            <Alert severity="error">{errorMessage(invitationsQuery.error)}</Alert>
          )}
          {!invitationsQuery.isLoading && invitations.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No pending invitations.
            </Typography>
          )}
          {invitations.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>
                      <Typography variant="body2">{inv.email}</Typography>
                      {inv.organization_name && (
                        <Typography variant="caption" color="text.secondary">
                          {inv.organization_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.purpose === "demo_reviewer" ? "Demo reviewer" : "Team member"}
                        size="small"
                        color={inv.purpose === "demo_reviewer" ? "info" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <RoleChip role={inv.role} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke invitation">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => revokeMutation.mutate(inv.id)}
                          disabled={revokeMutation.isPending}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Email deliverability status ─────────────────────────────────── */}
      <EmailStatusBanner />

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

function DemoReviewersCard({
  reviewers,
  isLoading,
  error,
  onToast,
  onChanged,
}: {
  reviewers: UserOut[];
  isLoading: boolean;
  error: unknown;
  onToast: (toast: { msg: string; severity: "success" | "error" }) => void;
  onChanged: () => void;
}) {
  const copyReminder = async (reviewer: UserOut) => {
    const firstName = (reviewer.full_name || reviewer.email).split(" ")[0];
    const sample = SAMPLE_LABELS[reviewer.demo_interest || "core"] || "Ledger Core";
    const message = [
      `Hi ${firstName} — here is your private Ledger Core review link:`,
      `${window.location.origin}/login?intent=demo`,
      `Choose Continue with Bynaus. I suggested the ${sample} sample; it uses dummy data only and is separate from every live company.`,
    ].join("\n\n");
    try {
      await navigator.clipboard.writeText(message);
      onToast({ msg: `Reminder copied for ${reviewer.full_name || reviewer.email}.`, severity: "success" });
    } catch {
      onToast({ msg: "The reminder could not be copied. Try again from a secure browser window.", severity: "error" });
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<VisibilityOutlinedIcon color="primary" />}
        title="Private demo reviewers"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
        subheader={
          reviewers.length
            ? `${reviewers.length} invited · ${reviewers.filter((reviewer) => reviewer.last_login_at).length} signed in`
            : "Track the people evaluating sample companies"
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading && <CircularProgress size={20} />}
        {!!error && <Alert severity="error">{errorMessage(error)}</Alert>}
        {!isLoading && !error && reviewers.length === 0 && (
          <Alert severity="info">
            No reviewers have been invited yet. Complete the private-demo invitation above; the reviewer will appear here immediately.
          </Alert>
        )}
        {reviewers.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reviewer</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Suggested sample</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Next action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewers.map((reviewer) => (
                <TableRow key={reviewer.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{reviewer.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{reviewer.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{reviewer.organization_name || "—"}</Typography>
                    {reviewer.job_title && (
                      <Typography variant="caption" color="text.secondary">{reviewer.job_title}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {SAMPLE_LABELS[reviewer.demo_interest || "core"] || "General accounting"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={reviewer.last_login_at ? "success" : "info"}
                      variant="outlined"
                      label={reviewer.last_login_at ? "Signed in" : "Invite sent"}
                    />
                    {reviewer.last_login_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {new Date(reviewer.last_login_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => void copyReminder(reviewer)}
                      >
                        Copy reminder
                      </Button>
                      <UserRowActions
                        user={reviewer}
                        isSelf={false}
                        onToast={onToast}
                        onChanged={onChanged}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Invite card ───────────────────────────────────────────────────────────────

function InviteCard({
  canInviteReviewers,
  onSuccess,
  onError,
}: {
  canInviteReviewers: boolean;
  onSuccess: (invite: InvitationResult) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "operator" | "viewer">("viewer");
  const [purpose, setPurpose] = useState<"team_member" | "demo_reviewer">(
    canInviteReviewers ? "demo_reviewer" : "team_member",
  );
  const [fullName, setFullName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [demoInterest, setDemoInterest] = useState("concrete_pumping");
  const [personalMessage, setPersonalMessage] = useState("");
  const [sentInvite, setSentInvite] = useState<InvitationResult | null>(null);
  const [copied, setCopied] = useState(false);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSend =
    emailIsValid &&
    (purpose === "team_member" || fullName.trim().length > 0);

  const inviteMutation = useMutation({
    mutationFn: () =>
      api<InvitationResult>("/api/auth/invitations", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          role: purpose === "demo_reviewer" ? "viewer" : role,
          purpose,
          full_name: fullName.trim() || null,
          organization_name: organizationName.trim() || null,
          job_title: jobTitle.trim() || null,
          demo_interest: purpose === "demo_reviewer" ? demoInterest : null,
          personal_message: personalMessage.trim() || null,
        }),
      }),
    onSuccess: (data) => {
      setSentInvite(data);
      onSuccess(data);
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const followUpText = (invite: InvitationResult) => {
    const firstName = (invite.full_name || invite.email).split(" ")[0];
    const sample = SAMPLE_LABELS[invite.demo_interest || "core"] || "Ledger Core";
    return [
      "Hi " + firstName + " — I just sent your private Ledger Core invitation.",
      "Use the password-setup email first, then open " +
        window.location.origin +
        "/login?intent=demo and choose Continue with Bynaus.",
      "I suggested the " + sample + " sample. It contains dummy data only and is separate from every live company.",
    ].join("\n\n");
  };

  const copyFollowUp = async (invite: InvitationResult) => {
    try {
      await navigator.clipboard.writeText(followUpText(invite));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onError("The follow-up message could not be copied. Try again from a secure browser window.");
    }
  };

  const resetBuilder = () => {
    setEmail("");
    setRole("viewer");
    setFullName("");
    setOrganizationName("");
    setJobTitle("");
    setDemoInterest("concrete_pumping");
    setPersonalMessage("");
    setSentInvite(null);
    setCopied(false);
  };

  if (sentInvite) {
    const isReviewer = sentInvite.purpose === "demo_reviewer";
    return (
      <Card
        variant="outlined"
        sx={{ borderColor: "success.light", bgcolor: "success.50", overflow: "hidden" }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
            <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 38 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Invitation sent
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                {sentInvite.full_name || sentInvite.email} will receive account-setup instructions at{" "}
                <strong>{sentInvite.email}</strong>.
              </Typography>
              <Alert severity="success" sx={{ mt: 2 }}>
                {isReviewer
                  ? "They get an isolated sample workspace with dummy data. No live company or bank access was granted."
                  : "They will receive the company role selected in this invitation."}
              </Alert>
              <Box sx={{ mt: 2.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {isReviewer && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => void copyFollowUp(sentInvite)}
                  >
                    {copied ? "Follow-up copied" : "Copy personal follow-up"}
                  </Button>
                )}
                <Button variant="outlined" onClick={resetBuilder}>
                  Invite another person
                </Button>
              </Box>
              {isReviewer && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  Send the copied note by text or your usual email so they know exactly where to begin.
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const isReviewer = purpose === "demo_reviewer";
  const sampleLabel = SAMPLE_LABELS[demoInterest] || "General accounting";

  return (
    <Card variant="outlined" sx={{ overflow: "hidden" }}>
      <CardHeader
        avatar={<PersonAddIcon color="primary" />}
        title="Create an invitation"
        titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
        subheader="Choose the kind of access first. Ledger Core handles the right setup and email."
        sx={{ px: { xs: 2.5, md: 3.5 }, pt: 3, pb: 2 }}
      />
      <CardContent sx={{ px: { xs: 2.5, md: 3.5 }, pt: 0, pb: 3.5 }}>
        {canInviteReviewers && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={purpose}
            onChange={(_event, value) => {
              if (value) setPurpose(value);
            }}
            aria-label="Invitation type"
            sx={{
              mb: 3,
              "& .MuiToggleButton-root": {
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: 1.5,
                px: 2,
                py: 1.75,
                textAlign: "left",
                textTransform: "none",
              },
            }}
          >
            <ToggleButton value="demo_reviewer" aria-label="Private demo reviewer">
              <VisibilityOutlinedIcon />
              <Box>
                <Typography variant="body2" fontWeight={700}>Private demo reviewer</Typography>
                <Typography variant="caption" color="text.secondary">
                  Dummy data only · progress tracked
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="team_member" aria-label="Company team member">
              <BusinessOutlinedIcon />
              <Box>
                <Typography variant="body2" fontWeight={700}>Company team member</Typography>
                <Typography variant="caption" color="text.secondary">
                  Access to live company books
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSend && !inviteMutation.isPending) inviteMutation.mutate();
          }}
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.35fr) minmax(280px, 0.65fr)" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              <TextField
                label={isReviewer ? "Full name" : "Full name (optional)"}
                required={isReviewer}
                size="small"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Dustin Smith"
                autoComplete="off"
              />
              <TextField
                label="Work email"
                required
                type="email"
                size="small"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="dustin@company.com"
                error={email.length > 0 && !emailIsValid}
                helperText={email.length > 0 && !emailIsValid ? "Enter a complete email address." : " "}
                autoComplete="off"
              />
              {isReviewer ? (
                <>
                  <TextField
                    label="Company (optional)"
                    size="small"
                    value={organizationName}
                    onChange={(event) => setOrganizationName(event.target.value)}
                    placeholder="Prospect company"
                    autoComplete="off"
                  />
                  <TextField
                    label="Title or role (optional)"
                    size="small"
                    value={jobTitle}
                    onChange={(event) => setJobTitle(event.target.value)}
                    placeholder="Owner, CFO, Controller…"
                    autoComplete="off"
                  />
                </>
              ) : (
                <FormControl size="small" sx={{ gridColumn: { sm: "span 2" } }}>
                  <InputLabel>Company role</InputLabel>
                  <Select
                    value={role}
                    label="Company role"
                    onChange={(event) => setRole(event.target.value as typeof role)}
                  >
                    <MenuItem value="viewer">Viewer — read-only access</MenuItem>
                    <MenuItem value="operator">Operator — create and edit records</MenuItem>
                    <MenuItem value="admin">Admin — full access including settings</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            {isReviewer && (
              <FormControl size="small" fullWidth>
                <InputLabel>Start them with</InputLabel>
                <Select
                  value={demoInterest}
                  label="Start them with"
                  onChange={(event) => setDemoInterest(event.target.value)}
                >
                  {SAMPLE_OPTIONS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Personal note (optional)"
              size="small"
              multiline
              minRows={2}
              value={personalMessage}
              onChange={(event) => setPersonalMessage(event.target.value)}
              placeholder={
                isReviewer
                  ? "I’d like you to explore the close workflow and tell us what matters for your business."
                  : "Welcome to the team."
              }
              inputProps={{ maxLength: 800 }}
              helperText={personalMessage.length + "/800 characters"}
            />

            <Button
              type="submit"
              size="large"
              variant="contained"
              startIcon={
                inviteMutation.isPending
                  ? <CircularProgress size={18} color="inherit" />
                  : <PersonAddIcon />
              }
              disabled={!canSend || inviteMutation.isPending}
              sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minWidth: 230 }}
            >
              {inviteMutation.isPending
                ? "Sending invitation…"
                : isReviewer
                  ? "Send private demo invite"
                  : "Send team invitation"}
            </Button>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: isReviewer ? "primary.light" : "warning.light",
              bgcolor: isReviewer ? "primary.50" : "warning.50",
              borderRadius: 2,
              p: 2.5,
              alignSelf: "stretch",
            }}
          >
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              What they receive
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
              {fullName.trim() ? fullName.trim().split(" ")[0] + "’s access" : "Invitation preview"}
            </Typography>
            {isReviewer ? (
              <Box component="ol" sx={{ pl: 2.5, mb: 0, color: "text.secondary" }}>
                <li><Typography variant="body2">A secure account-setup email</Typography></li>
                <li><Typography variant="body2">The {sampleLabel} sample-company picker</Typography></li>
                <li><Typography variant="body2">A guided CFO tour with progress preserved</Typography></li>
              </Box>
            ) : (
              <Box component="ol" sx={{ pl: 2.5, mb: 0, color: "text.secondary" }}>
                <li><Typography variant="body2">A secure account-setup email</Typography></li>
                <li><Typography variant="body2">{ROLE_LABELS[role]} access to the current company</Typography></li>
                <li><Typography variant="body2">The normal Ledger Core workspace</Typography></li>
              </Box>
            )}
            <Alert severity={isReviewer ? "success" : "warning"} sx={{ mt: 2 }}>
              {isReviewer
                ? "No live books, integrations, approvals, or bank connections."
                : "This grants access to real company data. Confirm the role before sending."}
            </Alert>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
// ── Email deliverability status ─────────────────────────────────────────────
// Single source of truth, computed by the backend from settings. Mailgun (HTTP
// API) counts as configured even though smtp_host is empty — so this never
// falsely claims "invitations won't be emailed" when Mailgun is live.

interface EmailStatus {
  can_send: boolean;
  transport: "mailgun" | "smtp" | "none";
  from_address: string;
}

function EmailStatusBanner() {
  const statusQuery = useQuery({
    queryKey: ["email-status"],
    queryFn: () => api<EmailStatus>("/api/admin/email-status"),
    staleTime: 60_000,
  });

  if (statusQuery.isLoading || !statusQuery.data) return null;
  const { can_send, transport, from_address } = statusQuery.data;

  if (!can_send) {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" href="/settings/integrations" component="a">
            Configure
          </Button>
        }
      >
        <strong>Invitations and password resets won't be emailed</strong> — no email
        transport is configured. Add a Mailgun key or SMTP settings under{" "}
        <a href="/settings/integrations" style={{ color: "inherit" }}>
          Setup → Integrations
        </a>
        . Until then you can copy the invite link from the server logs.
      </Alert>
    );
  }

  return (
    <Alert severity="success" icon={<EmailIcon fontSize="inherit" />}>
      Email sending is active via <strong>{transport === "mailgun" ? "Mailgun" : "SMTP"}</strong>
      {from_address ? <> — from <code>{from_address}</code></> : null}. Invitations and
      password resets will be delivered.
    </Alert>
  );
}

// ── Per-user row actions ────────────────────────────────────────────────────
// Change role, activate/deactivate, send password reset. Backend endpoints:
//   PATCH /api/users/{id}            { role | is_active }
//   POST  /api/users/{id}/reset-password
// Self-row destructive actions (demote / deactivate) are disabled so an admin
// can never lock themselves out.

const ROLE_ORDER: Array<"admin" | "operator" | "viewer"> = ["admin", "operator", "viewer"];

function UserRowActions({
  user,
  isSelf,
  onToast,
  onChanged,
}: {
  user: UserOut;
  isSelf: boolean;
  onToast: (t: { msg: string; severity: "success" | "error" }) => void;
  onChanged: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  const patch = useMutation({
    mutationFn: (body: { role?: string; is_active?: boolean }) =>
      api<UserOut>(`/api/users/${user.id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => onChanged(),
    onError: (err) => onToast({ msg: errorMessage(err), severity: "error" }),
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      api<void>(`/api/users/${user.id}/reset-password`, { method: "POST" }),
    onSuccess: () =>
      onToast({ msg: `Password-reset email sent to ${user.email}.`, severity: "success" }),
    onError: (err) => onToast({ msg: errorMessage(err), severity: "error" }),
  });

  const changeRole = (role: "admin" | "operator" | "viewer") => {
    close();
    if (role === user.role) return;
    patch.mutate(
      { role },
      { onSuccess: () => onToast({ msg: `${user.full_name} is now ${ROLE_LABELS[role]}.`, severity: "success" }) },
    );
  };

  const toggleActive = () => {
    close();
    const next = !user.is_active;
    patch.mutate(
      { is_active: next },
      {
        onSuccess: () =>
          onToast({
            msg: `${user.full_name} ${next ? "reactivated" : "deactivated"}.`,
            severity: "success",
          }),
      },
    );
  };

  const sendReset = () => {
    close();
    resetMutation.mutate();
  };

  const busy = patch.isPending || resetMutation.isPending;

  return (
    <>
      <Tooltip title="Manage user">
        <span>
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disabled={busy}
          >
            {busy ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        <Typography variant="overline" sx={{ px: 2, color: "text.secondary" }}>
          Change role
        </Typography>
        {ROLE_ORDER.map((r) => (
          <MenuItem
            key={r}
            selected={r === user.role}
            // Don't let an admin demote themselves out of admin.
            disabled={r === user.role || (isSelf && user.role === "admin" && r !== "admin")}
            onClick={() => changeRole(r)}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon
                fontSize="small"
                color={r === "admin" ? "error" : r === "operator" ? "warning" : "disabled"}
              />
            </ListItemIcon>
            <ListItemText>{ROLE_LABELS[r]}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={sendReset}>
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send password reset</ListItemText>
        </MenuItem>
        <Tooltip
          title={isSelf ? "You can't deactivate your own account" : ""}
          placement="left"
        >
          <span>
            <MenuItem onClick={toggleActive} disabled={isSelf}>
              <ListItemIcon>
                {user.is_active ? (
                  <BlockIcon fontSize="small" color="error" />
                ) : (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText>{user.is_active ? "Deactivate" : "Reactivate"}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
}
